import { Location } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { HeaderService } from 'core';
import { CustomerConfigurationService, CustomerConfigurationState } from '../../services/customer-configuration.service';

@Component({
  selector: 'ord-configure-customer-contacts',
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatFormFieldModule, MatInputModule, RouterLink, TranslocoModule],
  providers: [provideTranslocoScope('orders')],
  templateUrl: './configure-customer-contacts.html',
  styleUrl: './configure-customer-contacts.scss',
})
export class ConfigureCustomerContacts implements OnInit, OnDestroy {
  readonly configuration = inject(CustomerConfigurationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly headerService = inject(HeaderService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly proposalId = this.route.snapshot.queryParamMap.get('id');
  readonly customer = computed(() => this.configuration.proposalId() === this.proposalId ? this.configuration.customer() : null);
  // Nessun prefill da Cribis o dall'anagrafica: il contatto è inserito dall'utente.
  readonly form = inject(FormBuilder).nonNullable.group({
    nominativo: ['', [Validators.required, Validators.pattern(/\S/)]],
    ruolo: [''],
    telefono: [''],
    email: ['', [Validators.required, Validators.email]],
    note: [''],
  });

  ngOnInit(): void {
    this.headerService.removeActions();
    this.transloco.selectTranslate<string>('newCustomer.title', {}, 'orders')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(title => this.headerService.setTitle(title));
    this.configuration.restoreFlowState(this.location.getState() as Partial<CustomerConfigurationState> | null, this.proposalId);
    if (this.customer()) {
      // Ripristina esclusivamente dati già digitati in questa bozza, se si torna indietro nel flusso.
      this.form.setValue(this.configuration.contact());
    }
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.customer()) {
        this.configuration.contact.set(this.form.getRawValue());
      }
    });
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  continue(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || !this.customer() || !this.configuration.destinationForConfiguration()) {
      return;
    }
    const value = this.form.getRawValue();
    this.configuration.contact.set({
      nominativo: value.nominativo.trim(),
      ruolo: value.ruolo.trim(),
      telefono: value.telefono.trim(),
      email: value.email.trim(),
      note: value.note.trim(),
    });
    // TODO: Inviare i dati aggiunti dall'utente alla futura API.
    // Non vengono usate le API esistenti dei contatti Q8 durante la configurazione Cribis.
    void this.router.navigate(['/new-order-proposal/configure-customer/banking'], {
      queryParams: { id: this.proposalId },
      state: this.configuration.getFlowState(),
    });
  }
}
